/**
 * F43 (Track B) — Admin consumer-user detail.
 *
 * Route: /users/:id   (the F43 deep-link target for `subject_type='user'`)
 * Permission: users:read
 *
 * Header: Avatar + identity strip + status/role Badges + the inert player /
 * trainer ticks (F09 v2 `playerVerified` / `trainerVerified`). The Profile tab
 * carries the F09 `UserVerificationsCard` (every entity the user is or owns,
 * with deep links into `/verifications`). Left-rail
 * Tabs (Profile / Activity / Bookings / Teams / Club affiliations / Trainer /
 * Reviews & badges / Reports filed-by / Reports-against / Suspension history /
 * GDPR / Audit / Devices) — each tab a lazy read-through. Suspend/Restore Drawer
 * (mandatory reason + F26 notification-preview + destructive confirm; sends
 * `If-Match: detail.updatedAt`; renders a 409 STALE_USER stale-dialog). Internal
 * note Dialog. Per-device revoke (gated `users:device_revoke`).
 *
 * Data source: the F43 `adminUsersApi` (aliased to its fixture in tests). Every
 * affordance is gated by the actor's permissions (server re-enforces).
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  AuditLogTimeline,
  Avatar,
  Badge,
  Banner,
  Button,
  Card,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Skeleton,
  SuspendDialog,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  Textarea,
  useToast,
  type AuditEvent,
} from '@bravemove/ui-web';
import { ApiError } from '../../auth/types';
import { SUSPENSION_REASON_MAX, type AdminUserDetail, type DeviceToken } from '@bravemove/types';
import {
  adminUsersApi,
  type AdminUserAuditEntry,
  type AdminUserBadgeRow,
  type AdminUserGenericTab,
  type AdminUserNoteRow,
  type AdminUserReviewsBadgesTab,
  type AdminUserSkillRow,
  type AdminUserTabRow,
  type AdminUserTrainerTab,
} from '../../api/adminUsers';
import type {
  AdminModerateReviewInput,
  GdprRequest,
  ReportListItem,
  TrainerReviewAdmin,
} from '@bravemove/types';
import { useAuth } from '../../auth/useAuth';
import { translateApiError } from '../../auth/errors.js';
import { PageHeader } from '../../components/PageHeader';
import { UserSuspendDrawer } from '../../components/moderation/UserSuspendDrawer';
import { UserEditForm } from '../../components/moderation/UserEditForm';
import { UserSkillsPanel } from '../../components/moderation/UserSkillsPanel';
import { BookingsAdminPanel } from '../../components/moderation/BookingsAdminPanel';
import { TeamsAdminPanel } from '../../components/moderation/TeamsAdminPanel';
import { AffiliationsAdminPanel } from '../../components/moderation/AffiliationsAdminPanel';
import { useTrainerModeration } from '../../hooks/useTrainerModeration';
import { TrainerCertCard } from '../../components/trainer/TrainerCertCard';
import { TrainerReasonModal } from '../../components/trainer/TrainerReasonModal';
import { TrainerConfirmDialog } from '../../components/trainer/TrainerConfirmDialog';
import { adminRecommendationApi } from '../../api/recommendation';
import { ModerateReviewDialog } from '../../components/recommendation/ModerateReviewDialog';
import { ModerateEndorsementDialog } from '../../components/recommendation/ModerateEndorsementDialog';
import { adminB2bAccountApi } from '../../api/b2b-account';
import { InertTick } from '../../components/verification/EntityVerificationCard';
import { UserVerificationsCard } from '../../components/verification/UserVerificationsCard';
import { CreateActorDialog } from './CreateActorDialog';

/**
 * The 12 detail tabs, in render order. The active tab is persisted to the URL
 * (`?tab=`) so a refresh / deep-link restores it (mirrors the F42 venue detail
 * page). An unknown value falls back to 'profile'.
 */
const USER_TABS = [
  'profile',
  'skills',
  'bookings',
  'teams',
  'affiliations',
  'trainer',
  'reviews-badges',
  'reports-filed',
  'reports-against',
  'suspension-history',
  'gdpr',
  'audit',
  'devices',
  'notes',
] as const;

const STATUS_TONES: Record<string, 'success' | 'warning' | 'neutral' | 'danger' | 'info'> = {
  active: 'success',
  pending_verification: 'info',
  suspended: 'warning',
  disabled: 'neutral',
  deleted: 'danger',
};

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso;
  }
}

const AUDIT_ACTION_KIND: Record<string, AuditEvent['kind']> = {
  'user.suspend': 'alert',
  'user.restore': 'create',
  'user.note': 'info',
  'user.device_revoke': 'permission',
};

export function AdminPlatformUserDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const auth = useAuth();
  const toast = useToast();

  const canSuspend = auth.adminUser?.permissions?.includes('users:suspend') ?? false;
  const canNote = auth.adminUser?.permissions?.includes('users:note') ?? false;
  const canRevokeDevice = auth.adminUser?.permissions?.includes('users:device_revoke') ?? false;
  // F09 v2 — verification read/direct-verify gating lives inside
  // `UserVerificationsCard` (`verifications:read` / `:issue_privileged`).
  const canEdit = auth.adminUser?.permissions?.includes('users:edit') ?? false;
  const canVerifySkill = auth.adminUser?.permissions?.includes('users:skill_verify') ?? false;
  const canDelete = auth.adminUser?.permissions?.includes('users:delete') ?? false;
  const canForceTransition = auth.adminUser?.permissions?.includes('bookings:override') ?? false;
  const canModerateTeams = auth.adminUser?.permissions?.includes('teams:moderate') ?? false;
  const canReviewTrainer =
    auth.adminUser?.permissions?.includes('trainer_verifications:review') ?? false;
  const canModerateReviews = auth.adminUser?.permissions?.includes('reviews:moderate') ?? false;
  const canModerateEndorsements =
    auth.adminUser?.permissions?.includes('endorsements:moderate') ?? false;
  const canModerateAffiliations =
    auth.adminUser?.permissions?.includes('affiliations:moderate') ?? false;
  const canCreateActor = auth.adminUser?.permissions?.includes('b2b_accounts:create') ?? false;
  // FIX 5.22 (§13.11-7) — trainer billing (route gated `payments:read`) was
  // reachable only from a trainer statement row.
  const canReadBilling = auth.adminUser?.permissions?.includes('payments:read') ?? false;
  const [createActorOpen, setCreateActorOpen] = useState(false);

  // Active tab persisted in the URL (`?tab=`) — refresh/deep-link safe.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? '';
  const activeTab = (USER_TABS as readonly string[]).includes(tabParam) ? tabParam : 'profile';
  const handleTabChange = useCallback(
    (v: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', v);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const [detail, setDetail] = useState<AdminUserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [suspendMode, setSuspendMode] = useState<'suspend' | 'restore' | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [staleOpen, setStaleOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // Lazy per-tab state
  const [genericTab, setGenericTab] = useState<Record<string, readonly AdminUserTabRow[]>>({});
  const [trainer, setTrainer] = useState<AdminUserTrainerTab | null>(null);
  const [reviewsBadges, setReviewsBadges] = useState<AdminUserReviewsBadgesTab | null>(null);
  const [reportsAgainst, setReportsAgainst] = useState<readonly ReportListItem[] | null>(null);
  const [reportsFiled, setReportsFiled] = useState<readonly ReportListItem[] | null>(null);
  const [gdpr, setGdpr] = useState<readonly GdprRequest[] | null>(null);
  const [devices, setDevices] = useState<readonly DeviceToken[] | null>(null);
  const [audit, setAudit] = useState<readonly AdminUserAuditEntry[] | null>(null);
  const [skills, setSkills] = useState<readonly AdminUserSkillRow[] | null>(null);
  const [notes, setNotes] = useState<readonly AdminUserNoteRow[] | null>(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await adminUsersApi.getDetail(id);
      setDetail(d);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchDetail();
  }, [fetchDetail]);

  const fetchGenericTab = useCallback(
    async (tab: AdminUserGenericTab) => {
      if (genericTab[tab]) return;
      try {
        const res = await adminUsersApi.getTab(id, tab);
        setGenericTab((prev) => ({ ...prev, [tab]: res.data }));
      } catch {
        setGenericTab((prev) => ({ ...prev, [tab]: [] }));
      }
    },
    [id, genericTab],
  );

  // Load a tab's data lazily (idempotent — each branch guards on its own null
  // sentinel). Driven by the URL-tab effect below so a refresh / deep-link on
  // any tab loads its data, not just the tab the user clicked.
  const loadTab = useCallback(
    (tab: string) => {
      switch (tab) {
        case 'bookings':
        case 'teams':
        case 'affiliations':
        case 'suspension-history':
          void fetchGenericTab(tab);
          break;
        case 'trainer':
          if (trainer === null) {
            void adminUsersApi
              .getTrainer(id)
              .then((r) => setTrainer(r.data))
              .catch(() => setTrainer({ isTrainer: false, profile: null }));
          }
          break;
        case 'reviews-badges':
          if (reviewsBadges === null) {
            void adminUsersApi
              .getReviewsBadges(id)
              .then((r) => setReviewsBadges(r.data))
              .catch(() =>
                setReviewsBadges({ reviewsWritten: [], reviewsReceived: [], badges: [] }),
              );
          }
          break;
        case 'reports-filed':
          if (reportsFiled === null) {
            void adminUsersApi.getReportsFiled(id).then((r) => setReportsFiled(r.data));
          }
          break;
        case 'reports-against':
          if (reportsAgainst === null) {
            void adminUsersApi.getReportsAgainst(id).then((r) => setReportsAgainst(r.data));
          }
          break;
        case 'gdpr':
          if (gdpr === null) void adminUsersApi.getGdpr(id).then((r) => setGdpr(r.data));
          break;
        case 'devices':
          if (devices === null) void adminUsersApi.getDevices(id).then((r) => setDevices(r.data));
          break;
        case 'audit':
          if (audit === null) void adminUsersApi.getAudit(id).then((r) => setAudit(r.data));
          break;
        case 'skills':
          if (skills === null) {
            void adminUsersApi
              .getSkills(id)
              .then((r) => setSkills(r.data))
              .catch(() => setSkills([]));
          }
          break;
        case 'notes':
          if (notes === null) {
            void adminUsersApi
              .getNotes(id)
              .then((r) => setNotes(r.data))
              .catch(() => setNotes([]));
          }
          break;
        default:
          break;
      }
    },
    [
      id,
      fetchGenericTab,
      trainer,
      reviewsBadges,
      reportsFiled,
      reportsAgainst,
      gdpr,
      devices,
      audit,
      skills,
      notes,
    ],
  );

  // Re-fetch the skills tab after an admin skill edit (bypasses the null-guard).
  const reloadSkills = useCallback(() => {
    void adminUsersApi
      .getSkills(id)
      .then((r) => setSkills(r.data))
      .catch(() => setSkills([]));
  }, [id]);

  // Re-fetch the reviews & badges tab after an admin moderation (bypasses the
  // null-guard the lazy loader uses).
  const reloadReviewsBadges = useCallback(() => {
    void adminUsersApi
      .getReviewsBadges(id)
      .then((r) => setReviewsBadges(r.data))
      .catch(() => setReviewsBadges({ reviewsWritten: [], reviewsReceived: [], badges: [] }));
  }, [id]);

  // Re-fetch the notes tab (after posting a note).
  const reloadNotes = useCallback(() => {
    void adminUsersApi
      .getNotes(id)
      .then((r) => setNotes(r.data))
      .catch(() => setNotes([]));
  }, [id]);

  // Re-fetch the bookings tab after an admin force-transition (overwrites the
  // generic-tab cache so the short-circuit guard doesn't block the refresh).
  const reloadBookings = useCallback(() => {
    void adminUsersApi
      .getTab(id, 'bookings')
      .then((res) => setGenericTab((prev) => ({ ...prev, bookings: res.data })))
      .catch(() => setGenericTab((prev) => ({ ...prev, bookings: [] })));
  }, [id]);

  // Re-fetch the teams tab after an admin team action (member-remove / suspend /
  // disband). Overwrites the generic-tab cache to bypass the load short-circuit.
  const reloadTeams = useCallback(() => {
    void adminUsersApi
      .getTab(id, 'teams')
      .then((res) => setGenericTab((prev) => ({ ...prev, teams: res.data })))
      .catch(() => setGenericTab((prev) => ({ ...prev, teams: [] })));
  }, [id]);

  // Re-fetch the affiliations tab after an admin affiliation action (suspend /
  // end). Overwrites the generic-tab cache to bypass the load short-circuit.
  const reloadAffiliations = useCallback(() => {
    void adminUsersApi
      .getTab(id, 'affiliations')
      .then((res) => setGenericTab((prev) => ({ ...prev, affiliations: res.data })))
      .catch(() => setGenericTab((prev) => ({ ...prev, affiliations: [] })));
  }, [id]);

  // Delete a note (super-admin only — gated `users:delete`).
  const handleDeleteNote = useCallback(
    async (noteId: string) => {
      try {
        await adminUsersApi.deleteNote(id, noteId);
        reloadNotes();
        toast.push({
          tone: 'success',
          title: t('moderation:user.notes.deleted', { defaultValue: 'Note deleted.' }),
        });
      } catch (err) {
        toast.push({ tone: 'danger', title: translateApiError(err, t) });
      }
    },
    [id, reloadNotes, toast, t],
  );

  // Load the active tab's data once the detail is ready, and on every tab
  // change. A refresh on `?tab=trainer` lands here with the correct activeTab.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!loading && detail) loadTab(activeTab);
  }, [activeTab, loading, detail, loadTab]);

  const submitSuspendRestore = async (input: { reason?: string }) => {
    if (!detail) return;
    try {
      const next =
        suspendMode === 'suspend'
          ? await adminUsersApi.suspend(id, { reason: input.reason ?? '' }, detail.updatedAt)
          : await adminUsersApi.restore(id, detail.updatedAt);
      setDetail(next);
      toast.push({
        tone: 'success',
        title:
          suspendMode === 'suspend'
            ? t('moderation:user.suspend.title')
            : t('moderation:user.restore.title'),
      });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'STALE_USER') {
        setStaleOpen(true);
        await fetchDetail();
        return;
      }
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
      throw err;
    }
  };

  const handleDelete = async (reason: string) => {
    if (!detail) return;
    setDeleting(true);
    try {
      await adminUsersApi.deleteUser(id, reason, detail.updatedAt);
      setDeleteOpen(false);
      toast.push({
        tone: 'success',
        title: t('moderation:user.delete.done', { defaultValue: 'User permanently deleted.' }),
      });
      // The user no longer exists — leave the (now-dead) detail page.
      navigate('/users');
    } catch (err) {
      setDeleteOpen(false);
      if (err instanceof ApiError && err.code === 'STALE_USER') {
        setStaleOpen(true);
        await fetchDetail();
        return;
      }
      if (err instanceof ApiError && err.code === 'USER_HAS_DEPENDENCIES') {
        // The user has related data — surface why (the message lists the categories).
        toast.push({ tone: 'danger', title: err.message });
        return;
      }
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setDeleting(false);
    }
  };

  const submitNote = async () => {
    const trimmed = noteText.trim();
    if (trimmed.length === 0) return;
    setNoteSubmitting(true);
    try {
      await adminUsersApi.addNote(id, trimmed);
      toast.push({
        tone: 'success',
        title: t('moderation:user.note.saved', { defaultValue: 'Note saved.' }),
      });
      setNoteOpen(false);
      setNoteText('');
      // Reveal it: refresh + jump to the Notes tab so the admin sees their note.
      reloadNotes();
      handleTabChange('notes');
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setNoteSubmitting(false);
    }
  };

  const revokeDevice = async (tokenId: string) => {
    try {
      await adminUsersApi.revokeDevice(id, tokenId);
      const res = await adminUsersApi.getDevices(id);
      setDevices(res.data);
      toast.push({ tone: 'success', title: t('moderation:user.devices.revoke') });
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full rounded-lg" />
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error || !detail) {
    return <Banner tone="danger">{error ?? t('moderation:errors.REPORT_NOT_FOUND')}</Banner>;
  }

  const auditEvents: readonly AuditEvent[] = (audit ?? []).map((a) => ({
    id: a.id,
    at: a.createdAt,
    kind: AUDIT_ACTION_KIND[a.action] ?? 'info',
    summary: t(`moderation:user.audit.${a.action}`, { defaultValue: a.action }),
    actor: { name: a.actorDisplay ?? a.actorAdminId ?? 'system' },
    // Notes carry their text in afterSnapshot.note (reason is null); everything
    // else shows the reason.
    details:
      a.action === 'user.note'
        ? ((a.afterSnapshot as { note?: string } | null)?.note ?? a.reason ?? undefined)
        : (a.reason ?? undefined),
  }));

  return (
    <div className="space-y-6" data-testid="admin-platform-user-detail">
      <PageHeader title={t('moderation:user.detail_title')} />

      {/* Identity strip */}
      <Card>
        <div className="flex flex-wrap items-start gap-4 p-4">
          <Avatar name={detail.displayName} src={detail.avatarUrl ?? undefined} size="lg" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-h4 font-semibold text-ink truncate">{detail.displayName}</h2>
              <Badge tone={STATUS_TONES[detail.status] ?? 'neutral'} size="sm">
                {t(`moderation:user.status.${detail.status}`, { defaultValue: detail.status })}
              </Badge>
              {/* F09 v2 (§13 row 21) — inert ticks from the booleans only. */}
              <InertTick
                verified={detail.playerVerified}
                entityType="player"
                testId="f43-detail-player-tick"
              />
              <InertTick
                verified={detail.trainerVerified}
                entityType="trainer"
                testId="f43-detail-trainer-tick"
              />
            </div>
            <p className="font-mono text-[12px] text-ink-3">{detail.email}</p>
            <div className="flex flex-wrap gap-1">
              {detail.roles.map((r) => (
                <Badge key={r} tone="neutral" size="sm">
                  {t(`admin:f43.role.${r}`, { defaultValue: r })}
                </Badge>
              ))}
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {canReadBilling && detail.roles.includes('trainer') ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/trainers/${encodeURIComponent(detail.id)}/billing`)}
                data-testid="f43-detail-trainer-billing"
              >
                {t('trainer-billing:title')}
              </Button>
            ) : null}
            {canEdit ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setEditOpen(true)}
                data-testid="f43-detail-edit"
              >
                {t('admin:f43.edit.title', { defaultValue: 'Edit' })}
              </Button>
            ) : null}
            {canCreateActor && detail.status !== 'deleted' ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setCreateActorOpen(true)}
                data-testid="f43fc-create-actor-button"
              >
                {t('admin:f43fc.createActor.title', { defaultValue: 'Create business' })}
              </Button>
            ) : null}
            {canSuspend && detail.status !== 'suspended' && detail.status !== 'deleted' ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setSuspendMode('suspend')}
                data-testid="f43-detail-suspend"
              >
                {t('moderation:user.suspend.confirm')}
              </Button>
            ) : null}
            {canSuspend && detail.status === 'suspended' ? (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setSuspendMode('restore')}
                data-testid="f43-detail-restore"
              >
                {t('moderation:user.restore.confirm')}
              </Button>
            ) : null}
            {canNote ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setNoteOpen(true)}
                data-testid="f43-detail-note"
              >
                {t('moderation:user.note.title')}
              </Button>
            ) : null}
            {canDelete && detail.status !== 'deleted' ? (
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setDeleteOpen(true)}
                data-testid="f43-detail-delete"
              >
                {t('moderation:user.delete.button', { defaultValue: 'Delete user' })}
              </Button>
            ) : null}
          </div>
        </div>
        {detail.status === 'suspended' && detail.suspensionReason ? (
          <div className="border-t border-outline px-4 py-2.5">
            <Banner tone="warning" data-testid="f43-detail-suspension-reason">
              {detail.suspensionReason}
            </Banner>
          </div>
        ) : null}
      </Card>

      <Tabs value={activeTab} defaultValue="profile" onValueChange={handleTabChange}>
        <TabList scrollable>
          <Tab value="profile">{t('admin:f43.tab.profile')}</Tab>
          <Tab value="skills">{t('admin:f43.tab.skills', { defaultValue: 'Skills' })}</Tab>
          <Tab value="bookings">{t('admin:f43.tab.bookings')}</Tab>
          <Tab value="teams">{t('admin:f43.tab.teams')}</Tab>
          <Tab value="affiliations">{t('admin:f43.tab.affiliations')}</Tab>
          <Tab value="trainer">{t('admin:f43.tab.trainer')}</Tab>
          <Tab value="reviews-badges">{t('admin:f43.tab.reviewsBadges')}</Tab>
          <Tab value="reports-filed">{t('admin:f43.tab.reportsFiled')}</Tab>
          <Tab value="reports-against">{t('admin:f43.tab.reportsAgainst')}</Tab>
          <Tab value="suspension-history">{t('admin:f43.tab.suspensionHistory')}</Tab>
          <Tab value="gdpr">{t('admin:f43.tab.gdpr')}</Tab>
          <Tab value="audit">{t('admin:f43.tab.audit')}</Tab>
          <Tab value="devices">{t('admin:f43.tab.devices')}</Tab>
          <Tab value="notes">{t('admin:f43.tab.notes', { defaultValue: 'Notes' })}</Tab>
        </TabList>

        {/* Profile */}
        <TabPanel value="profile">
          <Card>
            <dl className="grid gap-3 p-4 sm:grid-cols-2" data-testid="f43-profile-fields">
              <Field label={t('admin:f43.profile.email')} value={detail.email} />
              <Field label={t('admin:f43.profile.phone')} value={detail.phone ?? '—'} />
              <Field
                label={t('admin:f43.profile.emailVerified')}
                value={formatDate(detail.emailVerifiedAt, locale)}
              />
              <Field
                label={t('admin:f43.profile.phoneVerified')}
                value={formatDate(detail.phoneVerifiedAt, locale)}
              />
              <Field
                label={t('admin:f43.profile.lastLogin')}
                value={formatDate(detail.lastLoginAt, locale)}
              />
              <Field
                label={t('admin:f43.profile.joined')}
                value={formatDate(detail.createdAt, locale)}
              />
              <Field
                label={t('admin:f43.profile.googleLinked')}
                value={detail.googleSubject ? t('admin:f43.filter.yes') : '—'}
              />
              <Field label={t('admin:f43.profile.locale')} value={detail.locale} />
            </dl>
          </Card>

          {/* F09 v2 (E14) — every entity this user is or owns, with deep links
              to its `/verifications` entity page and open request. */}
          <div className="mt-4">
            <UserVerificationsCard
              userId={id}
              playerVerified={detail.playerVerified}
              trainerVerified={detail.trainerVerified}
            />
          </div>
        </TabPanel>

        {/* Skills — per-sport full CRUD (users:skill_verify) */}
        <TabPanel value="skills">
          <UserSkillsPanel
            rows={skills}
            userId={id}
            canEdit={canVerifySkill}
            onReload={reloadSkills}
            // Cross-batch CTA — the F31 per-user skill-ratings page (history +
            // admin override) had no inbound link from the user detail.
            onOpenSkillRatings={() => navigate(`/users/${id}/skill-ratings`)}
          />
        </TabPanel>

        {/* Notes — every internal note posted on this user (newest first) */}
        <TabPanel value="notes">
          <NotesPanel
            rows={notes}
            canNote={canNote}
            canDeleteNote={canDelete}
            onAddNote={() => setNoteOpen(true)}
            onDeleteNote={handleDeleteNote}
            formatAt={(iso) => formatDate(iso, locale)}
          />
        </TabPanel>

        {/* Bookings — dedicated panel: per-row Force transition (bookings:override) */}
        <TabPanel value="bookings">
          <BookingsAdminPanel
            rows={genericTab['bookings']}
            canForceTransition={canForceTransition}
            onAfterTransition={reloadBookings}
          />
        </TabPanel>

        {/* Teams — dedicated panel: per-row Remove (gated) + team-wide Suspend/Disband */}
        <TabPanel value="teams">
          <TeamsAdminPanel
            rows={genericTab['teams']}
            userId={id}
            userName={detail.displayName}
            canModerate={canModerateTeams}
            onReload={reloadTeams}
          />
        </TabPanel>

        {/* Affiliations — dedicated panel: per-row Suspend/End (affiliations:moderate) */}
        <TabPanel value="affiliations">
          <AffiliationsAdminPanel
            rows={genericTab['affiliations']}
            canModerate={canModerateAffiliations}
            onReload={reloadAffiliations}
          />
        </TabPanel>

        {/* Trainer — dedicated panel (read-only summary + reviewer moderation) */}
        <TabPanel value="trainer">
          <TrainerPanel
            data={trainer}
            userId={id}
            canReview={canReviewTrainer}
            trainerVerified={detail.trainerVerified}
          />
        </TabPanel>

        {/* Reviews & badges — three labelled sub-sections + inline moderation */}
        <TabPanel value="reviews-badges">
          <ReviewsBadgesPanel
            data={reviewsBadges}
            canModerateReviews={canModerateReviews}
            canModerateEndorsements={canModerateEndorsements}
            onReload={reloadReviewsBadges}
          />
        </TabPanel>

        {/* Reports filed-by */}
        <TabPanel value="reports-filed">
          <ReportRows rows={reportsFiled} navigate={navigate} />
        </TabPanel>

        {/* Reports-against */}
        <TabPanel value="reports-against">
          <ReportRows rows={reportsAgainst} navigate={navigate} />
        </TabPanel>

        {/* Suspension history */}
        <TabPanel value="suspension-history">
          <TabRows rows={genericTab['suspension-history']} userId={id} />
        </TabPanel>

        {/* GDPR */}
        <TabPanel value="gdpr">
          {gdpr === null ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : gdpr.length === 0 ? (
            <EmptyState title={t('admin:f43.empty.gdpr')} />
          ) : (
            <Card>
              <ul className="divide-y divide-outline" data-testid="f43-user-gdpr">
                {gdpr.map((g) => (
                  <li key={g.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                    <span className="text-[13px] text-ink">
                      {t(`moderation:gdpr.kind.${g.kind}`)}
                    </span>
                    <Badge tone="neutral" size="sm">
                      {t(`moderation:gdpr.status.${g.status}`)}
                    </Badge>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </TabPanel>

        {/* Audit */}
        <TabPanel value="audit">
          {audit === null ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : auditEvents.length === 0 ? (
            <EmptyState title={t('admin:f43.empty.audit')} />
          ) : (
            <div data-testid="f43-user-audit">
              <AuditLogTimeline events={auditEvents} />
            </div>
          )}
        </TabPanel>

        {/* Devices */}
        <TabPanel value="devices">
          {devices === null ? (
            <Skeleton className="h-24 w-full rounded-lg" />
          ) : devices.length === 0 ? (
            <EmptyState title={t('moderation:user.devices.empty')} />
          ) : (
            <Card>
              <ul className="divide-y divide-outline" data-testid="f43-user-devices">
                {devices.map((d) => (
                  <li
                    key={d.id}
                    className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5"
                    data-testid={`f43-device-${d.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-[13px] text-ink truncate">{d.deviceName ?? d.platform}</p>
                      <p className="font-mono text-[11px] text-ink-3">
                        {d.platform} · {d.status}
                      </p>
                    </div>
                    {canRevokeDevice && d.status === 'active' ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => void revokeDevice(d.id)}
                        data-testid={`f43-device-revoke-${d.id}`}
                      >
                        {t('moderation:user.devices.revoke')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </TabPanel>
      </Tabs>

      {/* Edit-all-fields drawer (super-admin only; users:edit) */}
      {canEdit ? (
        <Drawer
          open={editOpen}
          onClose={() => setEditOpen(false)}
          side="end"
          width={560}
          title={t('admin:f43.edit.drawerTitle', { defaultValue: 'Edit user' })}
        >
          <UserEditForm
            detail={detail}
            onSaved={(next) => setDetail(next)}
            onReload={() => {
              setEditOpen(false);
              void fetchDetail();
            }}
          />
        </Drawer>
      ) : null}

      {/* Suspend / restore drawer */}
      {suspendMode !== null ? (
        <UserSuspendDrawer
          open
          mode={suspendMode}
          displayName={detail.displayName}
          onClose={() => setSuspendMode(null)}
          onSubmit={submitSuspendRestore}
        />
      ) : null}

      {/* Create business — super_admin (b2b_accounts:create). Provisions a B2B
          actor on this user; refetch the detail so the new role appears. */}
      {canCreateActor ? (
        <CreateActorDialog
          open={createActorOpen}
          onClose={() => {
            setCreateActorOpen(false);
            void fetchDetail();
          }}
          ownerUserId={id}
          onCreate={(input) => adminB2bAccountApi.createAccount(input)}
          onCreated={() =>
            toast.push({
              tone: 'success',
              title: t('admin:f43fc.createActor.toast_created', {
                defaultValue: 'Business account created',
              }),
            })
          }
        />
      ) : null}

      {/* Hard delete — super-admin only; type-to-confirm + reason (users:delete) */}
      {canDelete ? (
        <SuspendDialog
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          onConfirm={handleDelete}
          loading={deleting}
          title={t('moderation:user.delete.title', {
            defaultValue: 'Permanently delete this user?',
          })}
          description={t('moderation:user.delete.description', {
            defaultValue:
              'Permanently removes the user and all their personal data. This cannot be undone. Only works if the user has no bookings, business, reviews, or other records — otherwise suspend them instead.',
          })}
          confirmPhrase="DELETE"
          confirmLabel={t('moderation:user.delete.confirm', { defaultValue: 'Delete permanently' })}
          reasons={[
            {
              value: 'user_request',
              label: t('moderation:user.delete.reason.userRequest', {
                defaultValue: 'User requested deletion',
              }),
            },
            {
              value: 'spam_or_abuse',
              label: t('moderation:user.delete.reason.spamOrAbuse', {
                defaultValue: 'Spam / abuse',
              }),
            },
            {
              value: 'duplicate_account',
              label: t('moderation:user.delete.reason.duplicate', {
                defaultValue: 'Duplicate account',
              }),
            },
            {
              value: 'policy_violation',
              label: t('moderation:user.delete.reason.policyViolation', {
                defaultValue: 'Policy violation',
              }),
            },
            {
              value: 'other',
              label: t('moderation:user.delete.reason.other', { defaultValue: 'Other' }),
            },
          ]}
          consequences={[
            t('moderation:user.delete.consequence.permanent', {
              defaultValue: 'Permanently removes the account — this cannot be undone.',
            }),
            t('moderation:user.delete.consequence.personalData', {
              defaultValue: 'Deletes their profile, devices, sessions, and notifications.',
            }),
            t('moderation:user.delete.consequence.emailFreed', {
              defaultValue: 'Frees their email for re-registration.',
            }),
            t('moderation:user.delete.consequence.blocked', {
              defaultValue:
                'Refused if the user has bookings, an owned business, reviews, or other records — suspend instead.',
            }),
          ]}
        />
      ) : null}

      {/* Internal note dialog */}
      <Dialog
        open={noteOpen}
        onClose={() => setNoteOpen(false)}
        onConfirm={() => void submitNote()}
        title={t('moderation:user.note.title')}
        confirmLabel={t('moderation:user.note.confirm')}
        loading={noteSubmitting}
      >
        <FormField label={t('moderation:user.note.label')}>
          <Textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            maxLength={SUSPENSION_REASON_MAX}
            placeholder={t('moderation:user.note.placeholder')}
            rows={4}
            data-testid="f43-note-text"
          />
        </FormField>
      </Dialog>

      {/* Stale-write dialog (409 STALE_USER) */}
      <Dialog
        open={staleOpen}
        onClose={() => setStaleOpen(false)}
        title={t('moderation:errors.STALE_USER')}
        description={t('moderation:errors.STALE_USER')}
        confirmLabel={t('admin:f43.bulk.done', { defaultValue: 'OK' })}
        onConfirm={() => setStaleOpen(false)}
      />
    </div>
  );
}

function Field({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-ink-3">{label}</dt>
      <dd className="text-[13px] text-ink">{value}</dd>
    </div>
  );
}

function TabRows({
  rows,
  userId,
}: {
  readonly rows: readonly AdminUserTabRow[] | undefined;
  readonly userId: string;
}) {
  const { t } = useI18n();
  void userId;
  if (rows === undefined) return <Skeleton className="h-24 w-full rounded-lg" />;
  // Defense-in-depth: a future shape drift degrades to an empty state instead
  // of a white-screen crash (the F43 broken-tab failure mode).
  if (!Array.isArray(rows) || rows.length === 0) {
    return <EmptyState title={t('admin:f43.empty.generic')} />;
  }
  return (
    <Card>
      <ul className="divide-y divide-outline" data-testid="f43-tab-rows">
        {rows.map((r) => (
          <li key={r.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
            <div className="min-w-0">
              {/* Suspension-history rows carry the raw audit action as the
                  title — translate via the PU-11 user.audit.* vocabulary
                  (any non-action title falls through via defaultValue). */}
              <p className="text-[13px] text-ink truncate">
                {t(`moderation:user.audit.${r.title}`, { defaultValue: r.title })}
              </p>
              {r.subtitle ? <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p> : null}
            </div>
            {r.status ? (
              <Badge tone="neutral" size="sm">
                {r.status}
              </Badge>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function NotesPanel({
  rows,
  canNote,
  canDeleteNote,
  onAddNote,
  onDeleteNote,
  formatAt,
}: {
  readonly rows: readonly AdminUserNoteRow[] | null;
  readonly canNote: boolean;
  readonly canDeleteNote: boolean;
  readonly onAddNote: () => void;
  readonly onDeleteNote: (noteId: string) => Promise<void>;
  readonly formatAt: (iso: string) => string;
}) {
  const { t } = useI18n();
  const [confirmNote, setConfirmNote] = useState<AdminUserNoteRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  if (rows === null) return <Skeleton className="h-24 w-full rounded-lg" />;

  const confirmDelete = async () => {
    if (!confirmNote) return;
    setDeleting(true);
    try {
      await onDeleteNote(confirmNote.id);
      setConfirmNote(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="f43-user-notes">
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-ink-3">
          {t('moderation:user.notes.subtitle', {
            defaultValue: 'Internal notes — visible to admins only.',
          })}
        </p>
        {canNote ? (
          <Button variant="secondary" size="sm" onClick={onAddNote} data-testid="f43-notes-add">
            {t('moderation:user.notes.add', { defaultValue: 'Add note' })}
          </Button>
        ) : null}
      </div>
      {rows.length === 0 ? (
        <EmptyState
          title={t('moderation:user.notes.empty', { defaultValue: 'No internal notes yet.' })}
        />
      ) : (
        <Card>
          <ul className="divide-y divide-outline">
            {rows.map((n) => (
              <li
                key={n.id}
                className="flex items-start justify-between gap-3 px-4 py-3"
                data-testid={`f43-note-${n.id}`}
              >
                <div className="min-w-0">
                  <p className="whitespace-pre-wrap text-[13px] text-ink">{n.note}</p>
                  <p className="mt-1 text-[11px] text-ink-3">
                    {n.author} · {formatAt(n.createdAt)}
                  </p>
                </div>
                {canDeleteNote ? (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfirmNote(n)}
                    data-testid={`f43-note-delete-${n.id}`}
                  >
                    {t('moderation:user.notes.delete', { defaultValue: 'Delete' })}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Dialog
        open={confirmNote !== null}
        onClose={() => setConfirmNote(null)}
        onConfirm={() => void confirmDelete()}
        loading={deleting}
        title={t('moderation:user.notes.deleteTitle', { defaultValue: 'Delete this note?' })}
        description={t('moderation:user.notes.deleteBody', {
          defaultValue: 'This permanently removes the internal note. This cannot be undone.',
        })}
        confirmLabel={t('moderation:user.notes.delete', { defaultValue: 'Delete' })}
      />
    </div>
  );
}

function TrainerPanel({
  data,
  userId,
  canReview,
  trainerVerified,
}: {
  readonly data: AdminUserTrainerTab | null;
  readonly userId: string;
  readonly canReview: boolean;
  /** F09 v2 — the trainer tick (`AdminUserDetail.trainerVerified`). */
  readonly trainerVerified: boolean | undefined;
}) {
  const { t } = useI18n();
  const toast = useToast();
  // The hook is inert unless the actor can review AND the user is a trainer — so
  // a read-only actor / non-trainer never issues the presigned-GET-logging detail
  // fetch (F43-FC §6.4).
  const mod = useTrainerModeration(canReview && data?.isTrainer ? userId : undefined);

  // F09 v2 — the F33 profile approve/reject is retired (the trainer tick is an
  // F09 request decided under /verifications). The panel keeps the per-cert
  // review + suspend / reinstate / force-deactivate moderation.
  const [reinstateOpen, setReinstateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [reasonDialog, setReasonDialog] = useState<
    | { kind: 'reject-cert'; certId: string }
    | { kind: 'revoke-cert'; certId: string }
    | { kind: 'suspend' }
    | { kind: 'force-deactivate' }
    | null
  >(null);

  if (data === null) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (!data.isTrainer) {
    return (
      <EmptyState
        title={t('admin:f43.trainer.notTrainer', { defaultValue: 'This user is not a trainer.' })}
      />
    );
  }
  if (!data.profile) {
    return (
      <EmptyState
        title={t('admin:f43.trainer.noProfile', {
          defaultValue: 'Trainer role granted, but no trainer profile created yet.',
        })}
      />
    );
  }

  const p = data.profile;

  // Lightweight read-only summary — always shown (the getTrainer side-effect-free read).
  const summary = (
    <Card>
      <dl className="grid gap-3 p-4 sm:grid-cols-2" data-testid="f43-trainer-profile">
        <Field
          label={t('admin:f43.trainer.headline', { defaultValue: 'Headline' })}
          value={p.headline ?? '—'}
        />
        <Field
          label={t('admin:f43.trainer.years', { defaultValue: 'Years experience' })}
          value={p.yearsExperience != null ? String(p.yearsExperience) : '—'}
        />
        <Field
          label={t('admin:f43.trainer.acceptingNewClients', { defaultValue: 'Accepting clients' })}
          value={p.acceptingNewClients ? t('admin:f43.filter.yes', { defaultValue: 'Yes' }) : '—'}
        />
        <div>
          <dt className="text-[11px] uppercase tracking-wide text-ink-3">
            {t('admin:verification.integrations.trainerTick')}
          </dt>
          <dd className="text-[13px] text-ink" data-testid="f43-trainer-tick-field">
            {trainerVerified === true ? (
              <InertTick verified entityType="trainer" />
            ) : (
              t('admin:verification.entity.notVerified')
            )}
          </dd>
        </div>
        <div className="sm:col-span-2">
          <Field label={t('admin:f43.trainer.bio', { defaultValue: 'Bio' })} value={p.bio ?? '—'} />
        </div>
      </dl>
    </Card>
  );

  // Read-only actor → summary only (no moderation surface).
  if (!canReview) return summary;
  // Reviewer but the full detail is still loading / unavailable → summary + spinner.
  const detail = mod.detail;

  const handleReasonSubmit = async (reason: string): Promise<void> => {
    if (!reasonDialog) return;
    switch (reasonDialog.kind) {
      case 'reject-cert':
        await mod.rejectCert(reasonDialog.certId, reason);
        toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_rejected') });
        break;
      case 'revoke-cert':
        await mod.revokeCert(reasonDialog.certId, reason);
        toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_revoked') });
        break;
      case 'suspend':
        await mod.suspend(reason);
        toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.suspended') });
        break;
      case 'force-deactivate':
        await mod.forceDeactivate(reason);
        toast.push({
          tone: 'success',
          title: t('admin:trainer-verification.toast.force_deactivated'),
        });
        break;
    }
    setReasonDialog(null);
  };

  const handleReinstate = async () => {
    setBusy(true);
    setConfirmError(null);
    try {
      await mod.reinstate();
      setReinstateOpen(false);
      toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.reinstated') });
    } catch (err) {
      setConfirmError(translateApiError(err, t));
    } finally {
      setBusy(false);
    }
  };

  const handleVerifyCert = async (certId: string) => {
    try {
      await mod.verifyCert(certId);
      toast.push({ tone: 'success', title: t('admin:trainer-verification.toast.cert_verified') });
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    }
  };

  const isSuspended = detail !== null && detail.suspendedAt !== null;
  const isDeactivated = detail !== null && detail.deactivatedAt !== null;
  // FIX 4.13 — reinstate lifts a suspension or an admin force-deactivate lock.
  const canReinstate = isSuspended || (detail !== null && detail.adminDeactivatedAt != null);

  return (
    <div className="space-y-4" data-testid="f43-trainer-moderation">
      {summary}

      {detail === null ? (
        <Skeleton className="h-24 w-full rounded-lg" />
      ) : (
        <>
          {/* Certifications */}
          {detail.certifications.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="trainer-certs">
              {detail.certifications.map((cert) => (
                <TrainerCertCard
                  key={cert.id}
                  cert={cert}
                  busy={mod.busyCertId === cert.id}
                  canAct
                  onExpired={mod.handleLinkExpired}
                  onVerify={() => void handleVerifyCert(cert.id)}
                  onReject={() => setReasonDialog({ kind: 'reject-cert', certId: cert.id })}
                  onRevoke={() => setReasonDialog({ kind: 'revoke-cert', certId: cert.id })}
                />
              ))}
            </div>
          ) : null}
          {mod.linksExpired ? (
            <Button variant="secondary" size="sm" onClick={() => void mod.fetchDetail()}>
              {t('admin:trainer-verification.detail.refresh_links')}
            </Button>
          ) : null}

          {/* Moderation bar — suspend / reinstate / force-deactivate */}
          <Card>
            <div className="flex flex-wrap gap-2" data-testid="trainer-moderation-bar">
              {canReinstate ? (
                <Button
                  variant="primary"
                  onClick={() => {
                    setConfirmError(null);
                    setReinstateOpen(true);
                  }}
                  data-testid="trainer-reinstate"
                >
                  {t('admin:trainer-verification.detail.reinstate')}
                </Button>
              ) : (
                <Button
                  variant="destructive"
                  onClick={() => setReasonDialog({ kind: 'suspend' })}
                  data-testid="trainer-suspend"
                >
                  {t('admin:trainer-verification.detail.suspend')}
                </Button>
              )}
              {!isDeactivated ? (
                <Button
                  variant="secondary"
                  onClick={() => setReasonDialog({ kind: 'force-deactivate' })}
                  data-testid="trainer-force-deactivate"
                >
                  {t('admin:trainer-verification.detail.force_deactivate')}
                </Button>
              ) : null}
            </div>
          </Card>

          {/* Dialogs (shared components) */}
          <TrainerConfirmDialog
            open={reinstateOpen}
            title={t('admin:trainer-verification.detail.reinstate_confirm_title')}
            body={t('admin:trainer-verification.detail.reinstate_confirm_body')}
            confirmLabel={t('admin:trainer-verification.detail.reinstate')}
            loading={busy}
            error={confirmError}
            onClose={() => setReinstateOpen(false)}
            onConfirm={() => void handleReinstate()}
          />
          <TrainerReasonModal
            open={reasonDialog?.kind === 'reject-cert'}
            title={t('admin:trainer-verification.detail.cert_reject_title')}
            reasonLabel={t('admin:trainer-verification.detail.cert_reason_label')}
            reasonPlaceholder={t('admin:trainer-verification.detail.cert_reason_placeholder')}
            confirmLabel={t('admin:trainer-verification.detail.cert_reject')}
            destructive
            onClose={() => setReasonDialog(null)}
            onSubmit={handleReasonSubmit}
          />
          <TrainerReasonModal
            open={reasonDialog?.kind === 'revoke-cert'}
            title={t('admin:trainer-verification.detail.cert_revoke_title')}
            reasonLabel={t('admin:trainer-verification.detail.cert_reason_label')}
            reasonPlaceholder={t('admin:trainer-verification.detail.cert_reason_placeholder')}
            confirmLabel={t('admin:trainer-verification.detail.cert_revoke')}
            destructive
            onClose={() => setReasonDialog(null)}
            onSubmit={handleReasonSubmit}
          />
          <TrainerReasonModal
            open={reasonDialog?.kind === 'suspend'}
            title={t('admin:trainer-verification.detail.suspend_confirm_title')}
            description={detail.displayName}
            reasonLabel={t('admin:trainer-verification.detail.suspend_reason_label')}
            reasonPlaceholder={t('admin:trainer-verification.detail.suspend_reason_placeholder')}
            confirmLabel={t('admin:trainer-verification.detail.suspend')}
            destructive
            onClose={() => setReasonDialog(null)}
            onSubmit={handleReasonSubmit}
          />
          <TrainerReasonModal
            open={reasonDialog?.kind === 'force-deactivate'}
            title={t('admin:trainer-verification.detail.force_deactivate_confirm_title')}
            description={detail.displayName}
            reasonLabel={t('admin:trainer-verification.detail.force_deactivate_reason_label')}
            reasonPlaceholder={t(
              'admin:trainer-verification.detail.force_deactivate_reason_placeholder',
            )}
            confirmLabel={t('admin:trainer-verification.detail.force_deactivate')}
            destructive
            onClose={() => setReasonDialog(null)}
            onSubmit={handleReasonSubmit}
          />
        </>
      )}
    </div>
  );
}

function ReviewsBadgesPanel({
  data,
  canModerateReviews,
  canModerateEndorsements,
  onReload,
}: {
  readonly data: AdminUserReviewsBadgesTab | null;
  readonly canModerateReviews: boolean;
  readonly canModerateEndorsements: boolean;
  readonly onReload: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();

  // Review moderation: the row carries only the id → fetch the full admin shape
  // before opening the shared dialog (it needs .body/.status — spec §6.5).
  const [review, setReview] = useState<TrainerReviewAdmin | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewError, setReviewError] = useState<string | null>(null);

  // Endorsement moderation: only the id + a label are needed (no fetch).
  const [badge, setBadge] = useState<{ id: string; label: string; revoked: boolean } | null>(null);
  const [badgeError, setBadgeError] = useState<string | null>(null);

  const openReview = useCallback(
    async (rowId: string) => {
      setReviewError(null);
      try {
        const full = await adminRecommendationApi.reviewDetail(rowId);
        if (!full) {
          toast.push({ tone: 'danger', title: t('moderation:errors.REPORT_NOT_FOUND') });
          return;
        }
        setReview(full);
        setReviewOpen(true);
      } catch (err) {
        toast.push({ tone: 'danger', title: translateApiError(err, t) });
      }
    },
    [t, toast],
  );

  const confirmReview = useCallback(
    async (input: AdminModerateReviewInput) => {
      if (!review) return;
      setReviewError(null);
      try {
        await adminRecommendationApi.moderateReview(review.id, input);
        setReviewOpen(false);
        setReview(null);
        onReload();
        toast.push({
          tone: 'success',
          title: t('admin:f43.reviews.moderated', { defaultValue: 'Review moderated.' }),
        });
      } catch (err) {
        // Keep the dialog open + surface the error (re-throw per the dialog contract).
        setReviewError(translateApiError(err, t));
        throw err;
      }
    },
    [review, onReload, t, toast],
  );

  const confirmBadge = useCallback(
    async (input: { action: 'remove' | 'restore'; reason: string }) => {
      if (!badge) return;
      setBadgeError(null);
      try {
        await adminRecommendationApi.moderateEndorsement(badge.id, input);
        setBadge(null);
        onReload();
        toast.push({
          tone: 'success',
          title: t('admin:f43.endorsement.moderated', { defaultValue: 'Badge updated.' }),
        });
      } catch (err) {
        setBadgeError(translateApiError(err, t));
        throw err;
      }
    },
    [badge, onReload, t, toast],
  );

  if (data === null) return <Skeleton className="h-24 w-full rounded-lg" />;

  const reviewSections: ReadonlyArray<{
    key: string;
    title: string;
    rows: readonly AdminUserTabRow[];
    moderatable: boolean;
  }> = [
    {
      key: 'written',
      title: t('admin:f43.reviews.written', { defaultValue: 'Reviews written' }),
      rows: data.reviewsWritten,
      moderatable: canModerateReviews,
    },
    {
      key: 'received',
      title: t('admin:f43.reviews.received', { defaultValue: 'Reviews received' }),
      rows: data.reviewsReceived,
      moderatable: canModerateReviews,
    },
  ];
  const badgeRows: readonly AdminUserBadgeRow[] = data.badges;

  if (reviewSections.every((s) => s.rows.length === 0) && badgeRows.length === 0) {
    return <EmptyState title={t('admin:f43.empty.generic')} />;
  }

  return (
    <div className="space-y-4" data-testid="f43-reviews-badges">
      {reviewSections.map((s) => (
        <div key={s.key}>
          <h3 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-3">
            {s.title}
          </h3>
          {s.rows.length === 0 ? (
            <EmptyState title={t('admin:f43.empty.generic')} />
          ) : (
            <Card>
              <ul className="divide-y divide-outline">
                {s.rows.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                    <div className="min-w-0">
                      <p className="text-[13px] text-ink truncate">{r.title}</p>
                      {r.subtitle ? (
                        <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      {r.status ? (
                        <Badge tone="neutral" size="sm">
                          {r.status}
                        </Badge>
                      ) : null}
                      {s.moderatable ? (
                        <Button
                          variant="secondary"
                          size="xs"
                          onClick={() => void openReview(r.id)}
                          data-testid={`f43-review-moderate-${r.id}`}
                        >
                          {t('admin:f43.reviews.moderate', { defaultValue: 'Moderate' })}
                        </Button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      ))}

      <div>
        <h3 className="mb-1.5 text-[12px] font-semibold uppercase tracking-wide text-ink-3">
          {t('admin:f43.reviews.badges', { defaultValue: 'Badges' })}
        </h3>
        {badgeRows.length === 0 ? (
          <EmptyState title={t('admin:f43.empty.generic')} />
        ) : (
          <Card>
            <ul className="divide-y divide-outline">
              {badgeRows.map((r) => (
                <li key={r.id} className="flex items-center justify-between gap-2 px-4 py-2.5">
                  <div className="min-w-0">
                    <p className="text-[13px] text-ink truncate">{r.title}</p>
                    {r.subtitle ? (
                      <p className="text-[12px] text-ink-3 truncate">{r.subtitle}</p>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-2">
                    {r.status ? (
                      <Badge tone="neutral" size="sm">
                        {r.status}
                      </Badge>
                    ) : null}
                    {/* Revoke/Restore only on CATALOG badges (legacy/F09 system
                        badges 404 via adminModerate by design — spec §6.5). */}
                    {canModerateEndorsements && r.isCatalogBadge ? (
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={() =>
                          setBadge({
                            id: r.id,
                            label: r.title,
                            revoked: r.status === 'revoked',
                          })
                        }
                        data-testid={`f43-badge-moderate-${r.id}`}
                      >
                        {r.status === 'revoked'
                          ? t('admin:f43.endorsement.action.restore', { defaultValue: 'Restore' })
                          : t('admin:f43.endorsement.action.remove', { defaultValue: 'Revoke' })}
                      </Button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>

      <ModerateReviewDialog
        open={reviewOpen}
        review={review}
        onConfirm={confirmReview}
        onClose={() => {
          setReviewOpen(false);
          setReview(null);
          setReviewError(null);
        }}
        errorMessage={reviewError}
      />

      <ModerateEndorsementDialog
        open={badge !== null}
        badge={badge}
        onConfirm={confirmBadge}
        onClose={() => {
          setBadge(null);
          setBadgeError(null);
        }}
        errorMessage={badgeError}
      />
    </div>
  );
}

function ReportRows({
  rows,
  navigate,
}: {
  readonly rows: readonly ReportListItem[] | null;
  readonly navigate: (path: string) => void;
}) {
  const { t } = useI18n();
  if (rows === null) return <Skeleton className="h-24 w-full rounded-lg" />;
  if (rows.length === 0) return <EmptyState title={t('admin:f43.empty.reports')} />;
  return (
    <Card>
      <ul className="divide-y divide-outline" data-testid="f43-report-rows">
        {rows.map((r) => (
          <li
            key={r.id}
            className="flex cursor-pointer items-center justify-between gap-2 px-4 py-2.5 hover:bg-surface-2"
            onClick={() => navigate(`/reports/${r.id}`)}
            data-testid={`f43-report-row-${r.id}`}
          >
            <div className="min-w-0">
              <p className="text-[13px] text-ink truncate">
                {t(`moderation:report.subject_type.${r.subjectType}`)}
              </p>
              <p className="text-[12px] text-ink-3 truncate">
                {t(`moderation:report.reason.${r.reason}`)}
              </p>
            </div>
            <Badge tone="neutral" size="sm">
              {t(`moderation:report.status.${r.status}`)}
            </Badge>
          </li>
        ))}
      </ul>
    </Card>
  );
}
